import { useEffect, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useOidcContext } from '../contexts/OidcContext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { api } from '../api';
import { AiQuotaStatusOut, AppApiPortalPortalSchemasUsageStatsOut as ChatUsage, MonthlyUsageOut as MonthlyUsage } from '../types/apiRequests';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTranslation } from '../i18n/useTranslation';
import { TranslationKey } from '../i18n/en';

const EUR = (v: number | undefined) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v ?? 0);
const NUM = (v: number) => new Intl.NumberFormat('nl-NL').format(v);
const DATE_FULL = (d: string) => new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
const MONTH_LABEL = (m: string) => {
  const [year, month] = m.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
};


function CostSummaryRow({ label, value, sub, budget, color }: {
  label: string; value: number; sub?: string; budget?: number | null; color: string;
}) {
  const pct = budget && budget > 0 ? Math.min((value / budget) * 100, 100) : null;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{EUR(value)}</Typography>
          {sub && <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>{sub}</Typography>}
        </Box>
      </Box>
      {pct !== null && (
        <>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.06)', mt: 0.5 }}>
            <Box sx={{ height: '100%', borderRadius: 2, bgcolor: pct > 80 ? '#E86E44' : color, width: `${pct}%`, transition: 'width 0.4s ease' }} />
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(pct)}% van limiet ({EUR(budget!)})
          </Typography>
        </>
      )}
    </Box>
  );
}

function AiQuotaCard({
  status,
  loading,
  error,
  t,
}: {
  status: AiQuotaStatusOut | null;
  loading: boolean;
  error: boolean;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}) {
  if (loading) return <Skeleton variant="rounded" height={100} />;

  if (error) {
    return (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>
        {t('usage.aiQuota.unavailable')}
      </Typography>
    );
  }

  const spent = status?.spent_eur ?? 0;
  const quota = status?.quota_eur ?? 0;
  const unmetered = !quota || quota <= 0;
  const pct = unmetered ? null : Math.min((spent / quota) * 100, 100);
  const isHot = pct !== null && pct >= 80;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>
          {t('usage.aiQuota.spent')}
        </Typography>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {EUR(spent)}
        </Typography>
      </Box>
      {unmetered ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '999px', bgcolor: 'rgba(0,115,77,0.08)', mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#00734D', fontWeight: 600 }}>
            {t('usage.aiQuota.unmetered')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', mt: 0.5 }}>
            <Box sx={{
              height: '100%',
              borderRadius: 3,
              bgcolor: isHot ? '#E86E44' : '#18181b',
              width: `${pct}%`,
              transition: 'width 0.4s ease',
            }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
              {t('usage.aiQuota.percentOfQuota', { pct: Math.round(pct ?? 0) })}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
              {t('usage.aiQuota.quota')}: {EUR(quota)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.75 }}>
            {t('usage.aiQuota.helper')}
          </Typography>
        </>
      )}
    </Box>
  );
}

interface BreakdownItem { label: string; cost: number; color: string; budget: number; }

const BREAKDOWN_MAX_VISIBLE = 3;

function CostBar({ item }: { item: BreakdownItem }) {
  const hasBudget = item.budget > 0;
  const maxVal = hasBudget ? item.budget : item.cost || 0.001;
  const pct = Math.min((item.cost / maxVal) * 100, 100);
  const overBudget = hasBudget && pct > 80;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{EUR(item.cost)}</Typography>
      </Box>
      <Box sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(0,0,0,0.05)' }}>
        <Box sx={{
          height: '100%',
          borderRadius: 5,
          bgcolor: overBudget ? '#E86E44' : item.color,
          width: `${Math.max(pct, 1)}%`,
          minWidth: 4,
          transition: 'width 0.4s ease',
        }} />
      </Box>
      {hasBudget && (
        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(pct)}% van limiet ({EUR(item.budget)})
        </Typography>
      )}
    </Box>
  );
}

function BreakdownGroup({ title, items, t }: { title: string; items: BreakdownItem[]; t: (key: TranslationKey, params?: Record<string, string | number>) => string }) {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;
  // Biggest first, then cap at 3 per type with a show-more toggle so the list stays short.
  const sorted = [...items].sort((a, b) => b.cost - a.cost);
  const visible = showAll ? sorted : sorted.slice(0, BREAKDOWN_MAX_VISIBLE);
  const hidden = sorted.length - BREAKDOWN_MAX_VISIBLE;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</Typography>
      {visible.map((item) => <CostBar key={item.label} item={item} />)}
      {hidden > 0 && (
        <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 0.5, py: 0.25 }}>
          {showAll ? t('usage.breakdown.showLess') : t('usage.breakdown.showMore', { count: hidden })}
        </Button>
      )}
    </Box>
  );
}

function CostBreakdown({ usage, loading, t }: { usage: ChatUsage | null; loading: boolean; t: (key: TranslationKey, params?: Record<string, string | number>) => string }) {
  const theme = useTheme();
  if (loading) return <Skeleton variant="rounded" height={160} />;
  if (!usage) return null;

  const platformBudget = usage.platform_budget ?? 0;
  // Chat/AI spend is shown in the summary above, so it's omitted here. MCP and
  // workflow costs are split into their own capped groups.
  const workflowLabel = t('usage.breakdown.workflowLabelPrefix');
  const mcpItems: BreakdownItem[] = (usage.mcp_server_costs ?? []).map((s) => ({ label: s.server_name ?? '', cost: s.cost ?? 0, color: theme.palette.primary.main, budget: platformBudget }));
  const wfItems: BreakdownItem[] = (usage.workflow_costs ?? []).map((w) => ({
    label: `${workflowLabel} ${w.workflow_name ?? ''}`.trim(),
    cost: w.cost ?? 0,
    color: '#7c3aed',
    budget: 0,
  }));

  if (mcpItems.length === 0 && wfItems.length === 0) return (
    <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>{t('usage.breakdown.empty')}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <BreakdownGroup title={t('usage.breakdown.mcpTitle')} items={mcpItems} t={t} />
      <BreakdownGroup title={t('usage.breakdown.workflowTitle')} items={wfItems} t={t} />
    </Box>
  );
}

export default function UsagePage() {
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const [usage, setUsage] = useState<ChatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiQuota, setAiQuota] = useState<AiQuotaStatusOut | null>(null);
  const [aiQuotaLoading, setAiQuotaLoading] = useState(true);
  const [aiQuotaError, setAiQuotaError] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<MonthlyUsage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [platformBudget, setPlatformBudget] = useState('');
  const [workflowBudget, setWorkflowBudget] = useState('');
  const [aiHourlyLimit, setAiHourlyLimit] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getChatUsage(token);
      setUsage(data);
      setPlatformBudget(data.platform_budget != null ? String(data.platform_budget) : '');
      setWorkflowBudget(data.workflow_budget != null ? String(data.workflow_budget) : '');
      setAiHourlyLimit(
        data.ai_quota_eur_per_hour != null ? String(data.ai_quota_eur_per_hour) : ''
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const loadAiQuota = useCallback(async () => {
    setAiQuotaError(false);
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getAiQuotaStatus(token);
      // Skip state update when poll values are unchanged to avoid re-rendering for idle users.
      setAiQuota((prev) => {
        if (
          prev
          && prev.spent_eur === data.spent_eur
          && prev.quota_eur === data.quota_eur
          && prev.window_seconds === data.window_seconds
        ) {
          return prev;
        }
        return data;
      });
    } catch {
      // Surface a soft message rather than failing the whole page; backend logs the cause.
      setAiQuotaError(true);
    } finally {
      setAiQuotaLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isBeheerder) return undefined;
    loadUsage();
    loadAiQuota();
    // Refresh every 30s so the hourly window ticks down without a reload (single ZRANGEBYSCORE on the backend).
    const interval = window.setInterval(loadAiQuota, 30_000);
    return () => window.clearInterval(interval);
  }, [isBeheerder, loadUsage, loadAiQuota]);

  const handleOpenHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getUsageHistory(token);
      setHistory(data);
    } finally {
      setHistoryLoading(false);
    }
  };

  const MAX_BUDGET = 100000;

  const clampBudget = (value: string): number => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return 0;
    return Math.min(num, MAX_BUDGET);
  };

  const handleSaveBudget = async () => {
    setSavingBudget(true);
    try {
      const token = await getAccessTokenSilently();
      await api.updateBudget(token, {
        platform_budget: platformBudget ? clampBudget(platformBudget) : 0,
        workflow_budget: workflowBudget ? clampBudget(workflowBudget) : 0,
        // 0 clears the org override and falls back to PortalSettings.default_ai_quota_eur_per_hour.
        ai_quota_eur_per_hour: aiHourlyLimit ? clampBudget(aiHourlyLimit) : 0,
      });
      setBudgetOpen(false);
      loadUsage();
      loadAiQuota();
    } finally {
      setSavingBudget(false);
    }
  };

  if (!isBeheerder) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Card sx={{ maxWidth: 440, p: 5, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('usage.accessDenied.title')}</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>{t('usage.accessDenied.body')}</Typography>
          <Button onClick={() => navigate('/')} variant="outlined" size="small">{t('common.backToOverview')}</Button>
        </Card>
      </Box>
    );
  }

  const fixedCosts = (usage?.fixed_seat_cost ?? 0) + (usage?.fixed_mcp_cost ?? 0) + (usage?.fixed_workflow_cost ?? 0);
  const workflowCost = usage?.workflow_cost_total ?? 0;
  const totalCost = (usage?.mcp_cost_total ?? 0) + workflowCost + (usage?.chat_spend ?? 0) + fixedCosts;
  const hasData = !loading && (
    totalCost > 0
    || (usage?.mcp_requests_total ?? 0) > 0
    || (usage?.workflow_executions_total ?? 0) > 0
    || (usage?.daily?.length ?? 0) > 0
  );

  const btnSx = {
    borderColor: 'rgba(0,0,0,0.12)',
    color: 'text.secondary',
    textTransform: 'none' as const,
    fontWeight: 600,
    borderRadius: '8px',
    '&:hover': { borderColor: 'text.primary' },
  };

  return (
    <>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>{t('usage.page.title')}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{t('usage.page.subtitle')}</Typography>
          <Box sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.04)' }}>
            <CalendarMonthIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
              {t('usage.page.periodBanner', { period: new Date().toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }) })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: '16px !important' }} />}
            onClick={handleOpenHistory}
            sx={btnSx}
          >
            {t('usage.historyButton')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => setBudgetOpen(true)}
            sx={btnSx}
          >
            {t('usage.limitsButton')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 4, alignItems: 'start' }}>
        <Card sx={{ border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', boxShadow: 'none', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>{t('usage.dailyTable.title')}</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>{t('common.col.date')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.col.mcp')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.col.workflow')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.col.chat')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.col.total')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={5}><Skeleton variant="text" /></TableCell></TableRow>
              )) : (usage?.daily ?? []).slice().reverse().map((d) => (
                <TableRow key={d.date} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }}>{DATE_FULL(d.date)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(d.mcp_cost)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(d.workflow_cost)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(d.chat_cost)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600, py: 0.75 }} align="right">{EUR(d.total_cost)}</TableCell>
                </TableRow>
              ))}
              {!loading && (usage?.daily?.length ?? 0) === 0 && (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.disabled', fontSize: '0.8rem' }}>{t('usage.dailyTable.empty')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', boxShadow: 'none' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
              {t('usage.aiQuota.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mb: 2 }}>
              {t('usage.aiQuota.subtitle')}
            </Typography>
            <AiQuotaCard status={aiQuota} loading={aiQuotaLoading} error={aiQuotaError} t={t} />
          </Card>

          <Card sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', boxShadow: 'none' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', mb: 2 }}>{t('usage.costSummary.title')}</Typography>
            {loading ? <Skeleton variant="rounded" height={120} /> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <CostSummaryRow label={t('usage.costSummary.fixedCosts')} value={fixedCosts} color="#71717a" />
                <CostSummaryRow
                  label={t('usage.costSummary.platformCosts')}
                  value={usage?.mcp_cost_total ?? 0}
                  budget={usage?.platform_budget}
                  color="#18181b"
                />
                <CostSummaryRow
                  label={t('usage.costSummary.workflowCosts')}
                  value={workflowCost}
                  sub={
                    (usage?.workflow_executions_total ?? 0) > 0
                      ? t('usage.costSummary.executionsSuffix', { n: NUM(usage?.workflow_executions_total ?? 0) })
                      : undefined
                  }
                  budget={usage?.workflow_budget}
                  color="#7c3aed"
                />
                <CostSummaryRow
                  label={t('usage.costSummary.chatCosts')}
                  value={usage?.chat_spend ?? 0}
                  sub={usage?.chat_tokens ? `${NUM(usage.chat_tokens)} tokens` : undefined}
                  color="#00734D"
                />
                <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>{t('common.total')}</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{EUR(totalCost)}</Typography>
                </Box>
              </Box>
            )}
          </Card>

          <Card sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', boxShadow: 'none' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', mb: 2 }}>{t('usage.fixedCosts.title')}</Typography>
            {loading ? <Skeleton variant="rounded" height={80} /> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {t('usage.fixedCosts.seats', { count: String(usage?.seats_count ?? 0), price: EUR(usage?.cost_per_seat ?? 0) })}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {EUR(usage?.fixed_seat_cost ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {t('usage.fixedCosts.mcps', { count: String(usage?.mcp_count ?? 0), price: EUR(usage?.cost_per_mcp ?? 0) })}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {EUR(usage?.fixed_mcp_cost ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {t('usage.fixedCosts.workflows', { count: String(usage?.workflow_count ?? 0), price: EUR(usage?.cost_per_workflow ?? 0) })}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {EUR(usage?.fixed_workflow_cost ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>{t('usage.fixedCosts.subtotal')}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {EUR(fixedCosts)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Card>

          <Card sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', boxShadow: 'none' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', mb: 2 }}>{t('usage.breakdown.title')}</Typography>
            <CostBreakdown usage={usage} loading={loading} t={t} />
          </Card>
        </Box>
      </Box>

      {!loading && !hasData && (
        <Card sx={{ p: 6, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '14px', textAlign: 'center', boxShadow: 'none' }}>
          <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>{t('usage.empty')}</Typography>
        </Card>
      )}

      <Dialog open={budgetOpen} onClose={() => setBudgetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('usage.budgetDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label={t('usage.budgetDialog.platformLimit')}
              type="number"
              value={platformBudget}
              onChange={(e) => setPlatformBudget(e.target.value)}
              placeholder={t('usage.budgetDialog.noLimit')}
              size="small"
              fullWidth
              inputProps={{ min: 0, max: MAX_BUDGET, step: 0.01 }}
            />
            <TextField
              label={t('usage.budgetDialog.workflowLimit')}
              type="number"
              value={workflowBudget}
              onChange={(e) => setWorkflowBudget(e.target.value)}
              placeholder={t('usage.budgetDialog.noLimit')}
              size="small"
              fullWidth
              inputProps={{ min: 0, max: MAX_BUDGET, step: 0.01 }}
            />
            <TextField
              label={t('usage.budgetDialog.aiHourlyLimit')}
              type="number"
              value={aiHourlyLimit}
              onChange={(e) => setAiHourlyLimit(e.target.value)}
              placeholder={t('usage.budgetDialog.noLimit')}
              size="small"
              fullWidth
              inputProps={{
                min: 0,
                max: usage?.ai_quota_eur_per_hour_ceiling ?? MAX_BUDGET,
                step: 0.01,
              }}
              helperText={
                usage?.ai_quota_eur_per_hour_ceiling
                  ? t('usage.budgetDialog.aiHourlyLimitCeilingHint', {
                    ceiling: EUR(usage.ai_quota_eur_per_hour_ceiling),
                  })
                  : t('usage.budgetDialog.aiHourlyLimitHint')
              }
            />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
              {t('usage.budgetDialog.hint')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBudgetOpen(false)} color="inherit">{t('common.cancel')}</Button>
          <Button onClick={handleSaveBudget} variant="contained" disabled={savingBudget} disableElevation sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, fontWeight: 600 }}>
            {savingBudget ? t('common.savingButton') : t('common.saveButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('usage.historyDialog.title')}</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="text" height={32} />)}
            </Box>
          ) : history.length === 0 ? (
            <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem', mt: 1 }}>{t('usage.historyDialog.empty')}</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>{t('usage.historyDialog.col.month')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.seats')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.mcps')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.fixedCosts')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.platform')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.workflows')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.chat')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', py: 1 }} align="right">{t('usage.historyDialog.col.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.month} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.75, textTransform: 'capitalize' }}>{MONTH_LABEL(h.month)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75, color: 'text.secondary' }} align="right">
                      {h.seats_peak} &times; {EUR(h.cost_per_seat)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75, color: 'text.secondary' }} align="right">
                      {h.mcp_peak} &times; {EUR(h.cost_per_mcp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(h.total_fixed)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(h.total_platform)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(h.total_workflow_executions)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', py: 0.75 }} align="right">{EUR(h.total_chat)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', fontWeight: 700, py: 0.75 }} align="right">{EUR(h.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistoryOpen(false)} color="inherit">{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
